import React, { useEffect, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel, API } from '../../lib/api';
import { Plus, Check, X, Trash2, Receipt, Upload, FileText, IndianRupee } from 'lucide-react';
import { toast } from 'sonner';
import { inrCompact, formatDate } from '../../lib/format';

const TYPES = ['Travel', 'Fuel', 'Mobile', 'Food', 'Internet', 'Medical', 'Other'];
const STATUS_COLOR = {
  Draft: 'bg-slate-100 text-slate-700',
  Submitted: 'bg-amber-100 text-amber-700',
  Approved: 'bg-emerald-100 text-emerald-700',
  Rejected: 'bg-red-100 text-red-700',
  Paid: 'bg-blue-100 text-blue-700',
};

const fmt = (s) => formatDate(s);

export default function Reimbursements() {
  const [claims, setClaims] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ employee_id: '', claim_type: 'Travel', amount: '', claim_date: new Date().toISOString().slice(0, 10), description: '', project_id: '' });

  const load = async () => {
    try {
      const params = filterStatus ? `?status=${filterStatus}` : '';
      const [c, e] = await Promise.all([
        apiGet(`/hr/reimbursements${params}`),
        apiGet('/hr/employees'),
      ]);
      setClaims(c.claims || []);
      setEmployees(e);
    } catch (err) { console.error(err); }
  };
  useEffect(() => { load(); }, [filterStatus]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      await apiPost('/hr/reimbursements', payload);
      toast.success('Claim created');
      setShowForm(false);
      setForm({ employee_id: '', claim_type: 'Travel', amount: '', claim_date: new Date().toISOString().slice(0, 10), description: '', project_id: '' });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const callAction = async (claim, action, options = {}) => {
    try {
      if (action === 'submit') await apiPost(`/hr/reimbursements/${claim.id}/submit`);
      else if (action === 'approve') await apiPut(`/hr/reimbursements/${claim.id}/approve`);
      else if (action === 'reject') await apiPut(`/hr/reimbursements/${claim.id}/reject`);
      else if (action === 'paid') {
        const m = options.month || prompt('Payroll month (YYYY-MM)?', new Date().toISOString().slice(0, 7));
        if (!m) return;
        await apiPut(`/hr/reimbursements/${claim.id}/paid?payroll_month=${m}`);
      } else if (action === 'delete') {
        if (!window.confirm('Delete claim?\n\nThis action cannot be undone.')) return;
        await apiDel(`/hr/reimbursements/${claim.id}`);
      }
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)}d`);
      load();
      if (selected?.id === claim.id) {
        const refreshed = await apiGet(`/hr/reimbursements/${claim.id}`);
        setSelected(refreshed);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const uploadBill = async (claim, file) => {
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/hr/reimbursements/${claim.id}/bills`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      toast.success('Bill uploaded');
      const refreshed = await apiGet(`/hr/reimbursements/${claim.id}`);
      setSelected(refreshed);
      load();
    } catch (err) { toast.error('Upload failed'); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Payroll</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Reimbursement Claims</h1>
          <p className="text-sm text-slate-600 mt-1">Travel · Fuel · Mobile · Food · Medical — submit → approve → pay via payroll.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="new-reim-btn">
          <Plus size={16} /> New Claim
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-sm p-3 mb-4 flex items-center gap-2">
        {['', 'Draft', 'Submitted', 'Approved', 'Rejected', 'Paid'].map(s => (
          <button key={s || 'all'} onClick={() => setFilterStatus(s)} className={`px-3 py-1 text-xs rounded-sm border ${filterStatus === s ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'border-slate-300 text-slate-600 hover:border-[#3b4872]'}`}>
            {s || 'All'}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full dense-table">
          <thead><tr><th>Claim #</th><th>Employee</th><th>Type</th><th>Date</th><th>Amount</th><th>Bills</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {claims.map(c => (
              <tr key={c.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelected(c)} data-testid={`claim-row-${c.id}`}>
                <td className="font-mono text-xs">{c.claim_no}</td>
                <td><div className="font-display font-semibold">{c.employee_name}</div><div className="text-xs text-slate-500">{c.emp_code}</div></td>
                <td className="text-xs">{c.claim_type}</td>
                <td className="text-xs">{fmt(c.claim_date)}</td>
                <td className="font-display font-bold">₹{(c.amount || 0).toLocaleString('en-IN')}</td>
                <td className="text-xs">{(c.bills || []).length}</td>
                <td><span className={`tag ${STATUS_COLOR[c.status] || ''}`}>{c.status}</span></td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex gap-1">
                    {c.status === 'Draft' && <button onClick={() => callAction(c, 'submit')} className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 rounded-sm" data-testid={`submit-claim-${c.id}`}>Submit</button>}
                    {c.status === 'Submitted' && <button onClick={() => callAction(c, 'approve')} className="text-xs px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded-sm" data-testid={`approve-claim-${c.id}`}>Approve</button>}
                    {c.status === 'Submitted' && <button onClick={() => callAction(c, 'reject')} className="text-xs px-2 py-0.5 bg-red-50 text-red-700 rounded-sm">Reject</button>}
                    {c.status === 'Approved' && <button onClick={() => callAction(c, 'paid')} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-sm" data-testid={`pay-claim-${c.id}`}>Mark Paid</button>}
                    {(c.status === 'Draft' || c.status === 'Rejected') && <button onClick={() => callAction(c, 'delete')} className="text-red-600 hover:text-red-700"><Trash2 size={12} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {!claims.length && <tr><td colSpan="8" className="p-8 text-center text-sm text-slate-500">No reimbursement claims.</td></tr>}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-lg w-full">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">New Reimbursement Claim</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              <div>
                <label className="label">Employee *</label>
                <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="input" required data-testid="reim-employee-select">
                  <option value="">Select</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{`${e.emp_id} — ${e.name}`}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Type *</label>
                  <select value={form.claim_type} onChange={(e) => setForm({ ...form, claim_type: e.target.value })} className="input">
                    {TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
                <div><label className="label">Date *</label><input type="date" value={form.claim_date} onChange={(e) => setForm({ ...form, claim_date: e.target.value })} className="input" required /></div>
                <div><label className="label">Amount (₹) *</label><input type="number" min="1" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="input" required data-testid="reim-amount-input" /></div>
                <div><label className="label">Project (optional)</label><input type="text" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} className="input" /></div>
              </div>
              <div><label className="label">Description</label><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" rows={3} /></div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">Create as Draft</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex">
          <button className="flex-1 bg-black/40" onClick={() => setSelected(null)} aria-label="Close" />
          <div className="w-full max-w-[560px] bg-slate-50 border-l border-slate-200 flex flex-col">
            <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between">
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-1">{selected.claim_no}</div>
                <h2 className="font-display text-2xl font-extrabold tracking-tight">₹{(selected.amount || 0).toLocaleString('en-IN')}</h2>
                <div className="text-sm text-slate-600 mt-0.5">{selected.claim_type} · {fmt(selected.claim_date)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="w-9 h-9 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-sm"><X size={18} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div className="bg-white border border-slate-200 rounded-sm p-4 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Employee</span><span className="font-display font-semibold">{selected.employee_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status</span><span className={`tag ${STATUS_COLOR[selected.status]}`}>{selected.status}</span></div>
                {selected.approver && <div className="flex justify-between"><span className="text-slate-500">Approver</span><span>{selected.approver}</span></div>}
                {selected.paid_in_payroll_month && <div className="flex justify-between"><span className="text-slate-500">Paid in Payroll</span><span className="font-mono">{selected.paid_in_payroll_month}</span></div>}
                {selected.description && <div className="border-t border-slate-100 pt-2 mt-2"><div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Description</div><div className="text-xs">{selected.description}</div></div>}
                {selected.reject_reason && <div className="border-t border-slate-100 pt-2 mt-2"><div className="text-[10px] uppercase tracking-wider text-red-700 mb-1">Reject reason</div><div className="text-xs text-red-700">{selected.reject_reason}</div></div>}
              </div>

              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="font-display font-bold text-sm">Bills</h3>
                  {(selected.status === 'Draft' || selected.status === 'Rejected') && (
                    <label className="text-xs cursor-pointer font-display font-bold uppercase tracking-wider text-[#3b4872] hover:underline flex items-center gap-1">
                      <Upload size={12} /> Upload
                      <input type="file" className="hidden" onChange={(e) => e.target.files[0] && uploadBill(selected, e.target.files[0])} />
                    </label>
                  )}
                </div>
                <ul className="divide-y divide-slate-100">
                  {(selected.bills || []).map(b => (
                    <li key={b.id} className="px-4 py-2.5 flex items-center gap-3 text-sm">
                      <FileText size={14} className="text-[#3b4872] shrink-0" />
                      <a href={`${API}/hr/reimbursements/bills/${b.id}/download`} target="_blank" rel="noreferrer" className="flex-1 font-display font-semibold text-[#0F172A] hover:underline truncate">{b.filename}</a>
                      <span className="text-xs text-slate-500">{Math.round((b.size || 0) / 1024)} KB</span>
                    </li>
                  ))}
                  {(selected.bills || []).length === 0 && <li className="p-4 text-center text-xs text-slate-500">No bills attached</li>}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
